/**
 * A finite lifetime for one request.
 *
 * `fetch` has no timeout of its own, so a server that accepts the connection
 * and then stops answering leaves the promise pending for as long as the tab
 * lives. Anything awaiting that promise inside a state machine stops there,
 * and whatever the stage was withholding stays withheld.
 *
 * Composed from an `AbortController` rather than `AbortSignal.timeout` or
 * `AbortSignal.any` so that an expiry stays distinguishable from the caller's
 * own cancellation: the two mean opposite things to a caller, and a fetch
 * layer that rewraps the abort into its own error loses the `TimeoutError`
 * name that would otherwise tell them apart.
 */

export type RequestDeadline = {
    /** Aborts on expiry and on the caller's own signal. Pass it to `fetch`. */
    readonly signal: AbortSignal;
    /** Whether the deadline fired, as opposed to the caller aborting. */
    readonly expired: () => boolean;
    /** Releases the timer and the listener on the caller's signal. */
    readonly clear: () => void;
};

/**
 * Starts a deadline of `timeoutMs`, racing a caller's signal against it rather
 * than letting one replace the other: a caller that cancels must not also
 * remove the guarantee that the request ends.
 */
export function startDeadline(
    timeoutMs: number,
    caller?: AbortSignal | null
): RequestDeadline {
    const controller = new AbortController();
    let expired = false;
    const timer = setTimeout(() => {
        expired = true;
        controller.abort();
    }, timeoutMs);

    const onCallerAbort = (): void => controller.abort();
    if (caller?.aborted) {
        controller.abort();
    } else {
        caller?.addEventListener('abort', onCallerAbort, { once: true });
    }

    return {
        signal: controller.signal,
        expired: () => expired,
        clear: () => {
            clearTimeout(timer);
            caller?.removeEventListener('abort', onCallerAbort);
        },
    };
}
